import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import config, { TURNSTILE_TEST_SITE_KEY } from '../config';
import { getApiErrorCode } from '../utils/errorUtils';
import { stripTokenParam } from '../utils/resetToken';
import { eventMirrorEndpoint } from './eventMirrorEndpoint';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * HTTP statuses that are an expected, benign outcome for this specific call
     * (e.g. 404 on an idempotent DELETE) and must NOT be reported to Sentry by
     * the response interceptor. The request still rejects; the caller handles it.
     */
    expectedStatuses?: number[];
    /**
     * Backend `error_code` values (see ApiErrorCode) that are an expected,
     * non-actionable outcome for this specific call and must NOT be reported to
     * Sentry. The sibling of `expectedStatuses`, one level finer: a whole status
     * is too blunt where the same status also carries faults worth paging on —
     * a 403 on /users/registrations is a blocked bot when it says
     * turnstile_verification_failed and a broken app-account role when it says
     * access_denied. Declared per call, so a request that did NOT carry a
     * user-solved token (the app-credential login) reports by construction.
     */
    expectedErrorCodes?: string[];
    /**
     * When true, the response interceptor must NOT treat a 401 on this request as a
     * session expiry (no auth-clear, no /signin redirect). For calls where a 401 is a
     * domain error (wrong current password) or authenticates the app, not the user.
     */
    skipAuthRedirect?: boolean;
  }
}

const DSN = process.env.REACT_APP_SENTRY_DSN;

/**
 * The client event mirror's endpoint (services/eventMirror.ts). It flushes
 * every 5 seconds for as long as a tab is open, and Sentry's default
 * `breadcrumbsIntegration` records every one of those as a `fetch` breadcrumb.
 * The ring holds 100, so after about eight minutes on one page EVERY breadcrumb
 * on an error report is an analytics flush and the actions that caused the
 * error have been evicted. Filtered below.
 */
const EVENT_MIRROR_ENDPOINT = eventMirrorEndpoint();

const isEventMirrorRequest = (breadcrumb: { category?: string; data?: { url?: unknown } }): boolean => {
  if (breadcrumb.category !== 'fetch' && breadcrumb.category !== 'xhr') return false;
  const url = breadcrumb.data?.url;
  if (typeof url !== 'string') return false;
  // Exactly this path, not a prefix of it. The admin console's own reader is
  // `/api/v1/admin/events`, which does NOT share this prefix -- the shape the
  // exactness actually guards is a future sibling like `/api/v1/events/summary`,
  // which is what the test uses. A prefix match would silence that too, and a
  // request an operator made is ordinary activity worth keeping in the ring.
  const path = url.split(/[?#]/)[0].replace(/\/+$/, '');
  return path === EVENT_MIRROR_ENDPOINT;
};

export function initMonitoring(): void {
  if (!DSN) return;

  Sentry.init({
    dsn: DSN,
    environment: process.env.REACT_APP_ENV ?? 'development',
    release: process.env.REACT_APP_RELEASE ?? 'dev',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
    ],
    tracesSampleRate: process.env.REACT_APP_ENV === 'production' ? 0.2 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    tracePropagationTargets: [config.baseURL],
    sendDefaultPii: false,
    // Defense-in-depth: strip a password-reset `token` from any URL Sentry captures
    // (navigation breadcrumbs and the event request URL). The primary guard is the
    // startup scrub in resetToken.ts.
    beforeBreadcrumb: (breadcrumb) => {
      // Dropped, not scrubbed: the mirror's own traffic tells a reader nothing
      // and costs the report every breadcrumb it displaces.
      if (isEventMirrorRequest(breadcrumb)) return null;
      const d = breadcrumb.data;
      if (d) {
        if (typeof d.to === 'string') d.to = stripTokenParam(d.to);
        if (typeof d.from === 'string') d.from = stripTokenParam(d.from);
        if (typeof d.url === 'string') d.url = stripTokenParam(d.url);
      }
      return breadcrumb;
    },
    beforeSend: (event) => {
      if (event.request?.url) event.request.url = stripTokenParam(event.request.url);
      return event;
    },
  });

  reportTurnstileTestKeyFallback();
}

/**
 * A deployed bundle that fell back to the always-pass TEST sitekey is a total
 * signup and login outage with no other signal: the widget renders and solves
 * happily, siteverify answers `invalid-input-response` against the real secret,
 * and the API's 403 carries REPORT_TO_SENTRY = False, so nothing is raised on
 * either side. CRA inlines REACT_APP_* at build time, so this can only be a
 * missing var in the deploy job — invisible until users cannot sign up.
 *
 * Gated on the deployed environment names rather than on REACT_APP_ENV merely
 * being set: local .env.local sets it to 'development', which would make this
 * fire on every dev boot and train everyone to ignore it.
 */
function reportTurnstileTestKeyFallback(): void {
  const env = process.env.REACT_APP_ENV;
  if (env !== 'staging' && env !== 'production') return;
  if (config.turnstileSiteKey !== TURNSTILE_TEST_SITE_KEY) return;

  Sentry.captureMessage(
    'Turnstile site key fell back to the test key in a deployed build — signup and login will fail',
    'error',
  );
}

/**
 * A flush of the client event mirror failed in a way that will not fix itself.
 *
 * Separate from `captureApiError` because the mirror does not go through
 * `services/api.ts` and has no `AxiosError` to describe: what it has is a
 * status class and a batch. Its caller decides WHEN this is worth an event —
 * a 422 on sight, anything else only after an unbroken run — so this reports
 * whatever it is handed and does no rate limiting of its own.
 *
 * `captureMessage` rather than `captureException`: there is no throw here and
 * no stack worth keeping (it would be this function and a `fetch` callback),
 * and the fingerprint below is what groups these anyway. A 422 is `error`
 * because only a code change ends it; a run of 5xx or network failures is
 * `warning` because the network usually comes back.
 */
export function captureMirrorFailure(detail: {
  status: number | 'network';
  consecutiveFailures: number;
  eventNames: string[];
}): void {
  if (!DSN) return;

  Sentry.captureMessage(
    `Client event mirror flush failed (${detail.status})`,
    {
      level: detail.status === 422 ? 'error' : 'warning',
      tags: { endpoint: '/api/v1/events', status: detail.status },
      extra: {
        consecutiveFailures: detail.consecutiveFailures,
        // The names, and nothing else from the batch: `properties` is
        // user-shaped data this module has no business copying into Sentry,
        // and the name is what points at the call site to fix.
        eventNames: detail.eventNames,
      },
      // Grouped by status class, so contract drift never lands in the same
      // issue as a bad afternoon on the network.
      fingerprint: ['event-mirror', String(detail.status)],
    },
  );
}

/** Attach (or clear) the current user on Sentry events. No PII beyond id + role. */
export function setMonitoringUser(user: { id: string; role?: string | null } | null): void {
  if (!DSN) return;
  Sentry.setUser(user ? { id: user.id, role: user.role ?? undefined } : null);
}

/**
 * Report an API error to Sentry with structured tags for clean grouping.
 * Skips expected/non-actionable cases (401 session expiry, request cancellations).
 */
export function captureApiError(error: unknown): void {
  if (!DSN) return;

  const err = error as AxiosError;
  const status = err?.response?.status;
  if (status === 401) return; // expected session expiry — handled by the interceptor
  if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
  // Skip statuses the caller declared as an expected, benign outcome (e.g. a 404
  // on an idempotent DELETE that the UI treats as success).
  if (typeof status === 'number' && err?.config?.expectedStatuses?.includes(status)) return;

  const errorCode = getApiErrorCode(error) ?? null;
  // Skip error codes the caller declared as an expected, non-actionable outcome
  // for that specific request (see expectedErrorCodes above). Deliberately not a
  // list of codes held here: whether a code is benign depends on which call
  // raised it, and only the call site knows.
  if (errorCode !== null && err?.config?.expectedErrorCodes?.includes(errorCode)) return;

  const endpoint = err?.config?.url ?? 'unknown';
  const method = (err?.config?.method ?? 'unknown').toUpperCase();

  Sentry.captureException(error, {
    tags: {
      endpoint,
      method,
      status: status ?? 'network',
      error_code: errorCode ?? 'none',
    },
    fingerprint: ['api', errorCode ?? String(status ?? 'network'), endpoint],
  });
}
